
import { defineComponent, PropType, computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import BtnIcon from '@/views/dashboard/_components/btn-icon/btn-icon.vue'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import LoadMoreButton from '@/components/load-more-button.vue'
import UnauthorizedTooltip from '@/views/dashboard/_components/unauthorized-tooltip/unauthorized-tooltip.vue'
import EditableField from '@/views/dashboard/_components/editable-field/editable-field.vue'
import TerminalIconName from '@/views/dashboard/_components/terminal-icon-name/terminal-icon-name.vue'
import IPColumn from '@/views/dashboard/_components/ip-column/IPColumn.vue'
import { HIDE_FUNC } from '@/common/const/hide-function-config'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import { TableDataRowState } from '@/services/table-memory-pagination'
import { ClientItem } from '@/views/dashboard/_type/client-item'
import { TerminalTypeOptions } from '@/views/dashboard/_enum/terminal-type'
import { GroupClientPageKey } from '@/views/dashboard/group/client/group-client-page.context'
import { getAuthByList } from '@/services/service-permission.service'
import { TerminalAuthStatus } from '@/views/dashboard/_enum/terminal-auth-status'
import { team } from '@/common/basis/root-injector'
import type { Info } from './client.service'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'

const HOUR_SEC = 3600
const MIN_SEC = 60

export default defineComponent({
  name: 'GroupClientTerminalListSection',
  components: {
    BtnIcon,
    GroupTreeSelector,
    LoadMoreButton,
    UnauthorizedTooltip,
    EditableField,
    TerminalIconName,
    IPColumn
  },
  props: {
    viewMode: {
      type: String as PropType<TableViewModeType>,
      required: true
    },
    rows: {
      type: Array as () => TableDataRowState<ClientItem>[],
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    pageRowCount: {
      type: Number,
      required: true
    },
    total: {
      type: Number,
      required: true
    },
    loadMoreConfig: {
      type: Object,
      default: null
    },
    loadMoreHasMore: {
      type: Boolean,
      default: false
    },
    loadMoreLoading: {
      type: Boolean,
      default: false
    },
    loadMoreCurrent: {
      type: Number,
      default: 0
    },
    loadMoreTotal: {
      type: Number,
      default: 0
    }
  },
  emits: ['load-more'],
  setup(props) {
    const { t } = useI18n()
    const listPage = inject(GroupClientPageKey)
    if (!listPage) {
      throw new Error('GroupClientTerminalListSection 须作为终端管理页的子组件使用（缺少 provide(GroupClientPageKey)）')
    }
    const page = listPage.data
    const listStatus = listPage.status
    const allTableColumns = listPage.AlltableColumns
    const modulePermissionKey = listPage.ModulePermissionKey

    const tableHeight = computed(() => (props.viewMode === TableViewMode.full ? '100%' : undefined))
    const loadMoreDirectiveMerged = computed(() =>
      props.loadMoreConfig || {
        callback: () => {},
        disabled: true,
        hasMore: false,
        loading: false,
        threshold: 10,
        delay: 100
      }
    )

    function handleSearch() {
      page.options.key_word = page.searchKeyword
      page.options.page = 1
      listPage.fetchData(page.options)
    }

    function handlerSort({ prop, order }: { prop: string; order: string }) {
      if (prop === 'service_count') return
      if (order === 'ascending') {
        page.options.asc = true
      } else {
        page.options.asc = false
      }
      page.options.sort = prop
      listPage.fetchData(page.options)
    }

    function handleCheckedColumnChange(val: string[]) {
      page.checkedColumns = val
      page.tableColumns = listPage.AlltableColumns.value.filter((column: { c_key: string }) =>
        val.includes(column.c_key)
      )
      localStorage.setItem('CHECKED_COLUMN_LOCAL', JSON.stringify(page.checkedColumns))
    }

    function downloadCSV() {
      listPage.clientService.download(0, page.options, t('groupClient.exportTerminalListPrefix'))
    }

    function openModal(type: string) {
      page.clientModal[type] = true
    }

    function upgradeClient() {
      listPage.executeTerminalAction({
        serviceMethod: listPage.clientService.upgradeTerminal.bind(listPage.clientService),
        actionName: t('groupClient.terminalActionUpgrade')
      })
    }

    function calcDisabledStatus() {
      if (page.state.selectCount > 0) {
        return listPage.clientService.getSelectedData().some(item => item.login_status !== 1)
      }
      return true
    }

    function getListByMemory() {
      page.options.useMemory = !page.options.useMemory
      page.options.page = 1
      listPage.fetchData(page.options)
    }

    function calcActionBtnDisableStatus() {
      if (page.total && !page.state.selectAll) {
        return false
      }
      return true
    }

    function togglePageSelect() {
      listPage.clientService.togglePageSelect()
    }

    function selectAll() {
      page.options.page = 1
      page.options.useMemory = false
      listPage.clientService.selectAll()
      listPage.fetchData(page.options)
    }

    function selectCancel() {
      page.options.useMemory = false
      listPage.clientService.cancelSelectAll()
      listPage.fetchData(page.options)
    }

    function handleSelectionChange(val: TableDataRowState<ClientItem>[], currentValue?: TableDataRowState<ClientItem>) {
      if (listPage.ignoreTableSelectionChangeFromSync.value) {
        return
      }
      listPage.clientService.mergeSelectedData(val, currentValue)
    }

    function handleCheckbox(row: TableDataRowState<ClientItem>) {
      listPage.clientService.mergeSelectedData(listPage.clientService.getSelectedData(), row)
    }

    async function reloadData() {
      await listPage.fetchData(page.options)
    }

    function handleClickName(row: ClientItem) {
      page.currentRow = row
      listPage.terminalInfoVisible.value = true
    }

    function handleClickServiceCount(row: ClientItem) {
      page.currentRow = row
      listPage.serviceManageVisible.value = true
    }

    function handleClickLocation(row: ClientItem) {
      page.currentRow = row
      listPage.locationInfoVisible.value = true
    }

    function openFleetMap() {
      listPage.fleetMapVisible.value = true
    }

    function calcOnlineTime(duration: number) {
      let outStr = ''
      if (duration > 0) {
        const h = Math.floor(duration / HOUR_SEC)
        outStr += h ? t('groupClient.durationHours', { n: h }) : ''
        const m = Math.floor((duration % HOUR_SEC) / MIN_SEC)
        outStr += m ? t('groupClient.durationMinutes', { n: m }) : ''
        const s = (duration % HOUR_SEC) % MIN_SEC
        outStr += s ? t('groupClient.durationSeconds', { n: s }) : ''
        return outStr
      }
      return '--'
    }

    function getRemarkOperatorName(row: ClientItem) {
      const ext = row as ClientItem & { remark_operator_name?: string; remark_by_name?: string }
      return ext.remark_operator_name || ext.remark_by_name || ''
    }

    function viewDefencePolicy(row: ClientItem) {
      page.currentRow = row
      listPage.clientService.fetchClientStrategy({ device_id: row.device_id }).then(res => {
        const strategy = {} as Info
        Object.keys(res.info).forEach(item => {
          strategy[item] = JSON.parse(res.info[item])
        })
        page.defenceStrategy = { ...res, info: strategy }
      })
      page.clientModal.MODAL_CLIENT_DEFENCE_POLICY = true
    }

    function deleteClientRow(row: ClientItem) {
      page.currentRow = row
      page.clientModal.MODAL_CLIENT_DELETE = true
    }

    function openRemoteAssist(row: ClientItem) {
      page.currentRow = row
      page.remoteAssistDialog = true
    }

    function isDisabled(row: ClientItem) {
      return getAuthByList(row.auth_status, listPage.ModulePermissionKey[row.area_type]) !== TerminalAuthStatus.normal
    }

    async function editUserMark(value: string, row: ClientItem, updateRemarkFlag: boolean = false) {
      const copyRow = { ...row }
      try {
        if (updateRemarkFlag) {
          await listPage.clientService.updateTerminalRemark({
            remark: value,
            device_id: copyRow.device_id,
            team_id: String(team.getCurrentTeam().team_id)
          })
        } else {
          await listPage.clientService.updateTerminalName({
            new_name: value,
            device_id: copyRow.device_id,
            team_id: String(team.getCurrentTeam().team_id)
          })
        }
      } catch {
        ElMessage.error(t('groupClient.operationFailed'))
      } finally {
        listPage.fetchData(page.options)
      }
    }

    return {
      IS_JP_ONLY_BUILD,
      page,
      listStatus,
      allTableColumns,
      modulePermissionKey,
      getAuthByList,
      getRemoteDisableTip: listPage.getRemoteDisableTip,
      terminalGroupIdSelected: listPage.terminalGroupIdSelected,
      openTerminalListDrawer: listPage.openTerminalListDrawer,
      tableHeight,
      loadMoreDirectiveMerged,
      TableViewMode,
      terminalTypeOptions: TerminalTypeOptions,
      HIDE_FUNC,
      handleSearch,
      handlerSort,
      handleCheckedColumnChange,
      downloadCSV,
      openModal,
      upgradeClient,
      calcDisabledStatus,
      getListByMemory,
      calcActionBtnDisableStatus,
      togglePageSelect,
      selectAll,
      selectCancel,
      handleSelectionChange,
      handleCheckbox,
      reloadData,
      handleClickName,
      handleClickServiceCount,
      handleClickLocation,
      openFleetMap,
      calcOnlineTime,
      getRemarkOperatorName,
      viewDefencePolicy,
      deleteClientRow,
      openRemoteAssist,
      isDisabled,
      editUserMark
    }
  },
  methods: {
    getTable() {
      return this.$refs.innerTable as any
    }
  }
})
